import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { AuthState, User } from "../../../../types/auth";

const initialState: AuthState = {
    isAuthenticated: false,
    user: null,
    error: null,
    loading: false,
};

// Hardcoded user for demonstration
const hardcodedUser: User = {
    id: 'hardcoded-user-123',
    email: 'user@example.com',
    firstName: 'John',
    lastName: 'Doe',
};

export const signup = createAsyncThunk<User, User, { rejectValue: string }>(
    "auth/signup",
    async (userData, { rejectWithValue }) => {
        try {
            if (!userData.password) {
                return rejectWithValue("Password is required for signup.");
            }
            // Simulate signup - just return the user data
            return userData;
        } catch (err: any) {
            const errorMessage = err instanceof Error ? err.message : "An unknown error occurred.";
            return rejectWithValue(errorMessage);
        }
    }
);

export const signin = createAsyncThunk<
    { user: any },
    { email: string; password: string },
    { rejectValue: string }
>("auth/signin", async (credentials, { rejectWithValue }) => {
    try {
        // Simulate signin - return hardcoded user
        return { user: hardcodedUser };
    } catch (err: any) {
        const errorMessage = err instanceof Error ? err.message : "An unknown error occurred.";
        return rejectWithValue(errorMessage);
    }
});

export const signInWithGoogle = createAsyncThunk<
    { redirectUrl: string; user: any },
    void,
    { rejectValue: string }
>("auth/signInWithGoogle", async (_, { rejectWithValue }) => {
    try {
        // Simulate Google signin
        return { redirectUrl: '/auth/callback', user: hardcodedUser };
    } catch (err: any) {
        console.error("Catch block error:", err);
        const errorMessage = err instanceof Error ? err.message : "An unexpected error occurred.";
        return rejectWithValue(errorMessage);
    }
});

export const logout = createAsyncThunk<void, { router: any }, { rejectValue: string }>(
    "auth/logout",
    async ({ router },  { rejectWithValue, dispatch }) => {
        try {
            dispatch(setUser(null));
            router.push("/login");
        } catch (err: any) {
            const errorMessage = err instanceof Error ? err.message : "An unknown error occurred.";
            return rejectWithValue(errorMessage);
        }
    }
);

export const fetchUser = createAsyncThunk("auth/fetchUser", async () => {
    // Return hardcoded user
    return hardcodedUser;
});

export const getUserProfile = createAsyncThunk(
"auth/fetchUserProfile",
async (userId: string, { rejectWithValue }) => {
    try {
        // Return hardcoded profile
        return { id: userId, subscription: "premium" };
    } catch (err: any) {
        const errorMessage = err instanceof Error ? err.message : "An unknown error occurred.";
        return rejectWithValue(errorMessage);
    }
}
);


const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        logout: (state) => {
            state.isAuthenticated = false;
            state.user = null;
            state.error = null;
        },
        setUser: (state, action: PayloadAction<any>) => {
            const userData = action.payload;
            if (userData) {
                state.user = {
                    id: userData.id,
                    email: userData.email,
                    firstName: userData.user_metadata?.firstName || userData.firstName || "",
                    lastName: userData.user_metadata?.lastName || userData.lastName || "",
                };
                state.isAuthenticated = true;
            }
            else {
                state.user = null;
                state.isAuthenticated = false;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(signup.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(signup.fulfilled, (state, action: PayloadAction<User>) => {
                state.loading = false;
                state.isAuthenticated = true;
                state.user = action.payload;
            })
            .addCase(signup.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? "Failed to sign up.";
            })
            .addCase(logout.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(logout.fulfilled, (state) => {
                state.loading = false;
                state.isAuthenticated = false;
                state.user = null;
            })
            .addCase(logout.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? "Failed to log out.";
            })
            .addCase(signin.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(signin.fulfilled, (state, action: PayloadAction<{ user: User }>) => {
                state.loading = false;
                state.isAuthenticated = true;
                state.user = action.payload.user;
            })
            .addCase(signin.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? "Failed to log in.";
            })
            .addCase(signInWithGoogle.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                signInWithGoogle.fulfilled,
                (state, action: PayloadAction<{ redirectUrl: string; user: any }>) => {
                    state.loading = false;
                    state.isAuthenticated = true;
                    state.user = action.payload.user;
                    state.error = null;
                }
            )
            .addCase(signInWithGoogle.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload ?? "Failed to sign in with Google.";
            })

            .addCase(getUserProfile.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(getUserProfile.fulfilled, (state, action: PayloadAction<any>) => {
                if (state.user) {
                    state.user.profile = action.payload;
            }})
            .addCase(getUserProfile.rejected, (state, action) => {
                state.loading = false;
                state.error = (action.payload as string) ?? "Failed to fetch user profile.";
            });
    },
});
export const { setUser } = authSlice.actions;

export default authSlice.reducer;